import { TRANSLATIONS } from "../i18n/translations";
import { getLang, localeFor } from "../i18n/state";

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(localeFor(), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function tr(key: string, params?: Record<string, string | number>): string {
  const dict = TRANSLATIONS[getLang()] ?? TRANSLATIONS.en;
  const value = dict[key] ?? TRANSLATIONS.en[key] ?? key;
  if (!params) return value;
  return value.replace(/\{(\w+)\}/g, (_, k) => (k in params ? String(params[k]) : `{${k}}`));
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return tr("time.now");
  if (mins < 60) return tr("time.minutesAgo", { n: mins });
  const hours = Math.round(mins / 60);
  if (hours < 24) return tr("time.hoursAgo", { n: hours });
  const days = Math.round(hours / 24);
  return tr("time.daysAgo", { n: days });
}

export function slaRemaining(
  iso: string | null | undefined,
  status?: string | null,
): { label: string; state: "ok" | "risk" | "breached" | "none" } {
  // Canceled tickets no longer have an SLA budget — the work is
  // abandoned, so the timer doesn't matter. Resolved/closed are
  // less clear-cut (the agent's SLA was already met) but the
  // operator-facing semantics are the same: surface a breach badge
  // only on tickets where the clock is still running.
  if (status === "canceled") return { label: "—", state: "none" };
  if (!iso) return { label: "—", state: "none" };
  const diff = new Date(iso).getTime() - Date.now();
  if (diff < 0) return { label: tr("sla.expired"), state: "breached" };
  const mins = Math.round(diff / 60_000);
  // At-risk threshold: less than an hour to go.
  const state: "ok" | "risk" = mins <= 60 ? "risk" : "ok";
  if (mins < 60) return { label: tr("sla.minutes", { n: mins }), state };
  const hours = Math.round(mins / 60);
  if (hours < 24) return { label: tr("sla.hours", { n: hours }), state };
  const days = Math.round(hours / 24);
  return { label: tr("sla.days", { n: days }), state };
}
